import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@/types';
import { Users } from 'lucide-react';
import { useUsers } from '@/store/user';
import { useAssignments } from '@/store/assignment';
import { useTenants } from '@/store/tenant';
import { Button } from '@/components/ui/Button';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { ComposedTable, AvatarCell, StatusCell, BadgeCell, ActionCell, type FilterDef } from '@/components/table';
import { PageLayout } from '@/components/page-layout';

export const UsersPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [userRoles, setUserRoles] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const listUsers = useUsers((state) => state.list);
  const listAssignments = useAssignments((state) => state.listForPrincipal);
  const { tenants, list: listTenants } = useTenants();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useLayout();

  // Server-side filter state — driven by ComposedTable callbacks
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [roleFilter, setRoleFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [showPlatformUsers, setShowPlatformUsers] = useState(false);

  // Debounce timer for search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filterDefs = useMemo<FilterDef[]>(() => [
    { type: 'text', key: 'role', label: 'Role ID', placeholder: 'e.g. platform_admin' },
    { type: 'radio', key: 'tenant', label: 'Tenant', options: ['All', ...tenants.map(t => t.name)] },
    { type: 'toggle', key: 'show_platform', label: 'Show Platform Users', description: 'Include platform tenant users', defaultValue: false },
  ], [tenants]);

  const fetchUsers = useCallback(async (
    searchVal: string,
    statusVal: string,
    roleVal: string,
    tenantVal: string,
    includePlatform: boolean = false,
  ) => {
    setLoading(true);
    try {
      const fetched = await listUsers(
        searchVal || undefined,
        tenantVal || undefined, // tenant_id
        statusVal !== 'All' ? statusVal : undefined,
        roleVal || undefined,
      );

      const platformTenantId = '00000000-0000-0000-0000-000000000000';
      const filtered = includePlatform
        ? fetched
        : fetched.filter(u => u.tenant_id !== platformTenantId && u.tenant_id !== 'platform');

      setUsers(filtered);

      // Fetch role assignments per user for the "Assigned Roles" column
      const rolesMap: Record<string, string[]> = {};
      await Promise.all(
        filtered.map(async (u) => {
          try {
            const assigns = await listAssignments(u.id, 'user');
            rolesMap[u.id] = assigns.map((a) => a.role_id);
          } catch {
            rolesMap[u.id] = [];
          }
        }),
      );
      setUserRoles(rolesMap);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [listUsers, listAssignments]);

  useEffect(() => {
    setBreadcrumbs([{ name: 'Users' }]);
  }, [setBreadcrumbs]);

  // Initial fetch
  useEffect(() => {
    listTenants();
    fetchUsers(search, statusFilter, roleFilter, tenantFilter, showPlatformUsers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler: search text changes — debounce 400 ms
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchUsers(value, statusFilter, roleFilter, tenantFilter, showPlatformUsers);
    }, 400);
  };

  // Handler: status tab clicked — immediate re-fetch
  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    fetchUsers(search, value, roleFilter, tenantFilter, showPlatformUsers);
  };

  // Handler: "More Filters" applied
  const handleFilterChange = (values: Record<string, string | string[] | boolean>) => {
    const newRole = (values['role'] as string) || '';
    const newTenantName = (values['tenant'] as string) || '';
    const isShowPlatform = Boolean(values['show_platform']);

    let newTenantId = '';
    if (newTenantName && newTenantName !== 'All') {
      const tenant = tenants.find(t => t.name === newTenantName);
      if (tenant) newTenantId = tenant.id;
    }

    setRoleFilter(newRole);
    setTenantFilter(newTenantId);
    setShowPlatformUsers(isShowPlatform);
    fetchUsers(search, statusFilter, newRole, newTenantId, isShowPlatform);
  };

  const columns = [
    {
      id: 'name',
      header: 'User Name',
      accessorKey: 'name',
      cell: ({ row }: any) => {
        const u = row.original as User;
        const idx = users.indexOf(u);
        return (
          <AvatarCell
            name={u.name || 'Unnamed User'}
            subtitle={u.email}
            index={idx}
            onClick={() => navigate(`/m/users/${u.id}`)}
          />
        );
      },
    },
    {
      id: 'tenant',
      header: 'Tenant',
      cell: ({ row }: any) => {
        const u = row.original as User;
        const isPlatform = u.tenant_id === '00000000-0000-0000-0000-000000000000' || u.tenant_id === 'platform';
        if (isPlatform) {
          return <span className="text-[13px] font-medium text-slate-600 dark:text-slate-300">Platform</span>;
        }
        const tenant = tenants.find(t => t.id === u.tenant_id);
        const name = tenant ? tenant.name : (u.tenant_id ? u.tenant_id : 'Global');
        return <span className="text-[13px] font-medium text-slate-600 dark:text-slate-300">{name}</span>;
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }: any) => {
        const u = row.original as User;
        const statusMap: Record<number, string> = { 0: 'Pending', 1: 'Active', 2: 'Inactive', 3: 'Locked' };
        const status = u.status !== undefined ? (statusMap[u.status] ?? 'Unknown') : (u.deleted_at ? 'Inactive' : 'Active');
        return <StatusCell status={status} />;
      },
    },
    {
      id: 'role',
      header: 'Assigned Roles',
      cell: ({ row }: any) => {
        const u = row.original as User;
        const roles = userRoles[u.id] || [];
        if (roles.length === 0) return <span className="text-xs text-slate-400 italic">None</span>;
        return (
          <div className="flex flex-wrap gap-1.5">
            {roles.map((r) => (
              <BadgeCell key={r} label={r} variant={r.includes('admin') ? 'purple' : 'default'} />
            ))}
          </div>
        );
      },
    },
    {
      id: 'created_at',
      header: 'Date Joined',
      cell: ({ row }: any) => {
        const u = row.original as User;
        if (!u.created_at) return <span className="text-[13px] text-slate-400">—</span>;
        const d = new Date(u.created_at);
        return (
          <span className="text-[13px] text-slate-500 dark:text-slate-400">
            {d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      meta: { align: 'right' as const },
      cell: ({ row }: any) => <ActionCell onClick={() => navigate(`/m/users/${row.original.id}`)} />,
    },
  ];

  const emptyState = (
    <div className="p-12 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
      <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
        <Users className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No users found</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">Start by creating your first user identity.</p>
      <Button className="mt-6 bg-[#1e293b] hover:bg-slate-800 text-white rounded-full px-6" onClick={() => navigate('/m/users/create')}>
        Create first user
      </Button>
    </div>
  );

  return (
    <PageLayout title="Users" description="Manage user identities and access control settings.">
      {loading && users.length === 0 ? (
        <div className="flex justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900"><Loader /></div>
      ) : (
        <ComposedTable
          data={users}
          columns={columns}
          searchPlaceholder="Search users by name or email..."
          statusOptions={['All', 'Active', 'Inactive', 'Pending', 'Locked']}
          statusField={(u: User) => {
            const statusMap: Record<number, string> = { 0: 'Pending', 1: 'Active', 2: 'Inactive', 3: 'Locked' };
            return u.status !== undefined ? (statusMap[u.status] ?? 'Unknown') : (u.deleted_at ? 'Inactive' : 'Active');
          }}
          filters={filterDefs}
          onSearchChange={handleSearchChange}
          onStatusChange={handleStatusChange}
          onFilterChange={handleFilterChange}
          searchQuery={search}
          onAdd={() => navigate('/m/users/create')}
          emptyState={emptyState}
          itemLabel="users"
        />
      )}
    </PageLayout>
  );
};

